/**
 * Pull a human-readable error message out of a Better-Auth client error.
 *
 * BA's error envelope shape isn't fully stable — depending on which plugin
 * raised the error you can get the real message at `error.message`,
 * `error.body.message`, `error.body.error`, or just a `statusText`. The
 * old code only checked `error.message` and fell back to "Something went
 * wrong", which obscured useful messages like "Missing CAPTCHA response"
 * or "User already exists." This walks every known shape.
 *
 * Also translates a handful of stable error CODES to friendlier copy so
 * users see "That email is already registered" instead of the BA-internal
 * "USER_ALREADY_EXISTS".
 */
export function authErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  const e = error as Record<string, unknown>;

  // Some BA errors expose a stable `code` we can translate.
  const code =
    pickString(e.code) ??
    pickString((e.body as Record<string, unknown> | undefined)?.code);
  if (code) {
    const friendly = CODE_TO_MESSAGE[code];
    if (friendly) return friendly;
  }

  // Walk the most common shapes in priority order.
  return (
    pickString(e.message) ??
    pickString((e.body as Record<string, unknown> | undefined)?.message) ??
    pickString((e.body as Record<string, unknown> | undefined)?.error) ??
    pickString(e.statusText) ??
    (typeof e.status === 'number' ? `Error ${e.status}.` : null) ??
    fallback
  );
}

function pickString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

const CODE_TO_MESSAGE: Record<string, string> = {
  MISSING_RESPONSE: 'Please complete the captcha first.',
  INVALID_TOKEN: "The captcha expired — solve it again and resubmit.",
  USER_ALREADY_EXISTS: 'That email is already registered. Try signing in instead.',
  INVALID_EMAIL_OR_PASSWORD: 'Email or password is incorrect.',
  EMAIL_NOT_VERIFIED:
    "Check your inbox — we sent a verification email. Click the link inside, then try again.",
  WEAK_PASSWORD: 'That password is too short or too common. Try something longer.',
};
